// The header's account link and the ad-free decision (plan §11.4; docs/decisions.md, "Remove Ads — 364 days"). Ads are
// decided from what this browser remembers of the account's period of Remove Ads (src/account/adFreeCache.ts), so guests
// never download Firebase. A visitor with anything remembered loads it, to check the period with the server: a refund,
// a transfer or its end ends it, and an old or unchecked record (the earlier "1" above all) is checked before it counts.
// This is the page's one entitlement subscription; its changes reach the page through the browser's record.
import { adFreeCacheState, cachedAdFree, onAdFreeChanged } from '../account/adFreeCache';
import { isAccountConfigured } from '../account/firebase';
import { lang, translate } from '../i18n';
import { removeAdsOffered } from './removeAds';

/** How long the ads wait for the server's answer about a record that doesn't count by itself (then they decide). */
export const CHECK_WAIT_MS = 3_000;

/**
 * Fills the header's account slot and reports changes of Remove Ads (also from other tabs, and when a period ends while
 * the page is open). Answers the ad decision: true or false at once, or, for a record that needs the server's check,
 * the check's answer (at most CHECK_WAIT_MS later).
 */
export function startAccountLink(onAdFree: (adFree: boolean) => void): () => boolean | Promise<boolean> {
  const remembered = adFreeCacheState();
  const configured = isAccountConfigured();
  const slot = document.getElementById('account-slot');
  const prefix = lang === 'ko' ? '/ko' : '';
  const render = (adFree: boolean) => {
    if (!slot || !configured) return;
    // While Remove Ads isn't offered, a visitor without a period in force gets no link here.
    if (!adFree && !removeAdsOffered) {
      slot.replaceChildren();
      return;
    }
    const link = document.createElement('a');
    if (adFree) {
      link.href = `${prefix}/account`;
      link.textContent = translate(lang, 'site.nav.account');
    } else {
      link.href = `${prefix}/purchase`;
      link.className = 'remove-ads-link';
      link.textContent = translate(lang, 'site.nav.removeAds');
    }
    if (location.pathname === link.pathname) link.setAttribute('aria-current', 'page');
    slot.replaceChildren(link);
  };
  render(cachedAdFree());
  onAdFreeChanged((adFree) => {
    render(adFree);
    onAdFree(adFree);
  });

  let firstAnswer: Promise<boolean> | null = null;
  if (configured && remembered !== 'none') {
    firstAnswer = new Promise<boolean>((resolve) => {
      void import('../account/entitlements').then(
        ({ subscribeEntitlements }) => {
          subscribeEntitlements((state) => {
            if (state.status !== 'checking') resolve(state.adFree);
          });
        },
        () => resolve(cachedAdFree()),
      );
    });
  }
  return () => {
    if (remembered !== 'stale' || !firstAnswer) return cachedAdFree();
    return Promise.race([
      firstAnswer,
      new Promise<boolean>((resolve) => setTimeout(() => resolve(cachedAdFree()), CHECK_WAIT_MS)),
    ]);
  };
}
